import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Trophy, Medal, Calendar, TrendingUp, Loader2, IndianRupee, CalendarDays, Users, Hash } from 'lucide-react';

const Leaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('revenue'); // 'revenue' or 'quantity'
  
  // Date range state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activePreset, setActivePreset] = useState('today');

  // Set initial dates to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchLeaderboard();
    }
  }, [fromDate, toDate]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.getLeaderboardByDateRange(fromDate, toDate);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const setPreset = (preset) => {
    setActivePreset(preset);
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday;
        break;
      case 'thisWeek':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = today;
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last7Days':
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        end = today;
        break;
      case 'last30Days':
        start = new Date(today);
        start.setDate(today.getDate() - 29);
        end = today;
        break;
      default:
        start = end = today;
    }

    setFromDate(start.toISOString().split('T')[0]);
    setToDate(end.toISOString().split('T')[0]);
  };

  const handleCustomDateChange = () => {
    setActivePreset('custom');
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={24} className="text-yellow-500" />;
    if (rank === 2) return <Medal size={24} className="text-slate-400" />;
    if (rank === 3) return <Medal size={24} className="text-amber-600" />;
    return <span className="text-lg font-bold text-slate-400">#{rank}</span>;
  };

  const getRankBg = (rank, hasSales) => {
    if (!hasSales) return 'bg-slate-50 border-slate-200 opacity-70';
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
    return 'bg-white border-slate-200';
  };

  const presetButtons = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'last7Days', label: 'Last 7 Days' },
    { key: 'last30Days', label: 'Last 30 Days' },
  ];

  const totalSales = data?.leaderboard?.reduce((sum, item) => sum + item.total_sales, 0) || 0;
  const totalRevenue = data?.leaderboard?.reduce((sum, item) => sum + item.total_revenue, 0) || 0;
  const callersWithSales = data?.leaderboard?.filter(item => item.total_sales > 0).length || 0;

  // Sort leaderboard based on view mode
  const sortedLeaderboard = data?.leaderboard ? [...data.leaderboard].sort((a, b) => {
    if (viewMode === 'revenue') {
      return b.total_revenue - a.total_revenue;
    } else {
      return b.total_sales - a.total_sales;
    }
  }).map((item, index) => ({ ...item, rank: index + 1 })) : [];

  return (
    <div data-testid="leaderboard-page" className="max-w-[1000px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings text-slate-900 flex items-center gap-3">
            <Trophy size={28} className="text-yellow-500" />
            Sales Leaderboard
          </h1>
          <p className="text-slate-600 font-body">Performance ranking by {viewMode === 'revenue' ? 'revenue' : 'conversions'}</p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('revenue')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'revenue'
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <IndianRupee size={16} />
            Revenue
          </button>
          <button
            onClick={() => setViewMode('quantity')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'quantity'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Hash size={16} />
            Quantity
          </button>
        </div>
      </div>

      {/* Date Range Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
        {/* Preset Buttons */}
        <div className="flex flex-wrap gap-2">
          {presetButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activePreset === key
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Custom Date Range */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-slate-500" />
            <span className="text-sm text-slate-600">Custom Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                handleCustomDateChange();
              }}
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                handleCustomDateChange();
              }}
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {!loading && data && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-emerald-100 text-sm uppercase mb-1">Active Callers</p>
              <p className="text-3xl font-bold flex items-center gap-2" data-testid="lb-active-callers">
                <Users size={24} />
                {callersWithSales}
              </p>
              <p className="text-[11px] text-emerald-100 mt-0.5">of {data.total_callers} total</p>
            </div>
            <div>
              <p className="text-emerald-100 text-sm uppercase mb-1">Avg Sales / Person</p>
              <p className="text-3xl font-bold" data-testid="lb-avg-sales">
                {callersWithSales > 0 ? (totalSales / callersWithSales).toFixed(1) : '0'}
              </p>
              <p className="text-[11px] text-emerald-100 mt-0.5 flex items-center gap-0.5">
                <IndianRupee size={11} />
                {callersWithSales > 0 ? Math.round(totalRevenue / callersWithSales).toLocaleString() : '0'} avg revenue
              </p>
            </div>
            <div>
              <p className="text-emerald-100 text-sm uppercase mb-1">Total Sales</p>
              <p className="text-3xl font-bold">{totalSales}</p>
            </div>
            <div>
              <p className="text-emerald-100 text-sm uppercase mb-1">Total Revenue</p>
              <p className="text-3xl font-bold flex items-center gap-1">
                <IndianRupee size={28} />
                {totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : data?.leaderboard?.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Trophy size={64} className="mx-auto text-slate-200 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Callers Found</h3>
          <p className="text-slate-500">No sales callers in the system yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedLeaderboard.map((item) => (
            <div
              key={item.caller_id}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 ${getRankBg(item.rank, item.total_sales > 0)} transition-all hover:shadow-md`}
            >
              <div className="w-12 h-12 flex items-center justify-center">
                {getRankIcon(item.rank)}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 text-lg">{item.caller_name}</h3>
                <p className="text-sm text-slate-500">
                  {item.total_sales > 0 
                    ? `${item.total_sales} sale${item.total_sales !== 1 ? 's' : ''} closed`
                    : 'No sales in this period'
                  }
                </p>
              </div>
              <div className="text-right">
                {viewMode === 'revenue' ? (
                  <>
                    <div className={`flex items-center gap-1 font-bold text-xl ${item.total_revenue > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <IndianRupee size={20} />
                      {item.total_revenue.toLocaleString()}
                    </div>
                    <p className="text-xs text-slate-500 uppercase">Revenue</p>
                  </>
                ) : (
                  <>
                    <div className={`font-bold text-xl ${item.total_sales > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                      {item.total_sales}
                    </div>
                    <p className="text-xs text-slate-500 uppercase">Conversions</p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
